/**
 * Egzersiz üreticisinin tür sözleşmesi.
 *
 * Tek bir üretim üç kanala birden hizmet eder: ekranda oynanır, A4'e basılır,
 * veli telefonunda açılır. Bunun mümkün olması için şablonlar "nasıl
 * çizilecek" demez, "ne var" der — çizim modeli soyut tutulur. HTML, canvas
 * ve PDF aynı modeli okur.
 *
 * GETAP'ın 10.500 sabit sayfası yerine buradaki yaklaşım: 20 şablon ×
 * parametre × tohum = pratikte sınırsız varyant. Aynı tohum aynı sayfayı
 * verir, farklı tohum yeni sayfa.
 */

import type { Domain } from '../types'
import type { ShapeName } from '../render/shapes'
import type { AlanKodu } from '../render/icons'

export type ExerciseCode =
  // Dikkat
  | 'scan_target'
  | 'spot_difference'
  | 'odd_one_out'
  | 'word_search'
  | 'path_trace'
  // Bellek
  | 'span_recall'
  | 'memory_pairs'
  | 'symbol_code'
  | 'listening_steps'
  | 'reading_target'
  // Akıl yürütme
  | 'matrix_pattern'
  | 'sequence_complete'
  | 'logic_grid'
  | 'sudoku_mini'
  | 'category_sort'
  // Uzamsal ve hız
  | 'maze'
  | 'mental_rotation'
  | 'mirror_image'
  | 'arith_fluency'
  | 'opposite_rule'

export type Channel = 'digital' | 'print'

/* ------------------------------------------------------------------ *
 * Çizim modeli — altı düğüm tipi, hepsi hem ekranda hem kağıtta çizilir
 * ------------------------------------------------------------------ */

export interface CellNode {
  /**
   * Hücrede görünen METİN: harf, rakam, kelime.
   *
   * Şekil buraya yazılmaz; şeklin yeri `shape`. İkisi aynı alanda
   * taşınabilirdi (şekil adları harf ve rakamlarla çakışmıyor) ama o
   * zaman "bu hücre ne gösteriyor" sorusunun cevabı türde değil,
   * çalışma anında bir arama tablosunda olurdu.
   */
  glyph?: string
  /**
   * Hücrede çizilen ŞEKİL.
   *
   * Geometrisi `render/shapes.ts`'te; ekran ve kâğıt aynı yoldan
   * çiziyor. Eskiden şekiller de `glyph` içinde Unicode karakter
   * olarak duruyordu ve görünüşleri yazı tipine bağlıydı — aynı
   * tohumun aynı sayfayı vermesi cihazlar arasında tutmuyordu.
   */
  shape?: ShapeName
  /** Doldurulacak boş kutu (cevap alanı) */
  blank?: boolean
  /** Vurgulu hücre (örnek, ipucu) */
  marked?: boolean
  /** Labirent duvarları: üst, sağ, alt, sol */
  walls?: [boolean, boolean, boolean, boolean]
  /** Hücre kimliği — dijital tarafta tıklama hedefi */
  id?: string
}

export type RenderNode =
  | { t: 'text'; value: string; size?: 'sm' | 'md' | 'lg'; bold?: boolean }
  | { t: 'grid'; cols: number; rows: number; cells: CellNode[]; gap?: number; border?: boolean }
  /** Serbest yerleşim: sembollerin x,y konumu (0–1 aralığında oran) */
  | {
      t: 'scatter'
      items: { glyph?: string; shape?: ShapeName; x: number; y: number; rot?: number; id?: string }[]
    }
  /**
   * Çizgiler (0–1 oranlı noktalar). Yol takibi gibi egzersizlerde
   * izlenecek şeyin kendisi. Sembolleri yerleştirip çizgileri unutmak,
   * çocuğa takip edilecek hiçbir şey olmayan bir sayfa vermek demekti.
   */
  | {
      t: 'paths'
      /**
       * `dash`: SVG stroke-dasharray değeri.
       *
       * İpucunu RENKTEN BAĞIMSIZ taşıyor. Renk tek başına yeterliydi
       * sanılıyordu; ev yazıcısı çoğunlukla siyah-beyaz bastığı için
       * kâğıtta ipucu sessizce kayboluyordu ve kolay olması gereken
       * seviye, olması gerekenden zor çıkıyordu. Çizgi biçimi gri
       * tonlamada da ayırt ediliyor.
       */
      items: { points: [number, number][]; id?: string; color?: string; dash?: string }[]
      labels?: { x: number; y: number; text: string; id?: string }[]
      height?: number
    }
  /** Anahtar/açıklama: sembol → karşılık */
  /**
   * Anahtar/açıklama: sembol → karşılık.
   *
   * `from` metin, `fromShape` şekil; biri dolu olur. Şifre
   * egzersizinin anahtarı şekillerden kuruluyor, yol takibininki
   * harflerden.
   */
  | { t: 'key'; pairs: { from?: string; fromShape?: ShapeName; to: string }[] }
  /**
   * Satır satır sorular (aritmetik vb.).
   *
   * `palette` verilirse cevap klavyeyle değil, verilen semboller arasından
   * DOKUNARAK girilir. Şekil dizisi isteyen bir egzersizi kağıtta çocuk
   * çizer, ekranda ise ▲ karakterini klavyede bulamaz — palet olmadan o
   * egzersiz ekranda cevaplanamaz hale geliyordu.
   */
  | {
      t: 'rows'
      /**
       * `label` satırın metni. Satırın kendisi bir ŞEKİLSE
       * (`symbol_code`'da soru sembolden ibaret) şekil `labelShape`'e
       * yazılır ve `label` boş kalır.
       */
      items: { label: string; labelShape?: ShapeName; blank: boolean; id?: string }[]
      columns?: number
      /**
       * Seçilebilir uyaranlar. Şekil paletinde `shape`, metin
       * paletinde `glyph` dolu olur; cevaba giden dize ikisinde de
       * `shape ?? glyph`.
       */
      palette?: { glyph?: string; shape?: ShapeName }[]
      /**
       * Paletten seçim satırı doldurur ('single', varsayılan) mı yoksa
       * sırayla eklenir mi ('sequence'). Dizi hatırlamada çocuk birden
       * çok sembol girer; grup ayırmada tek bir ad yeter.
       */
      paletteMode?: 'single' | 'sequence'
    }
  /** Seçenekler: çoktan seçmeli */
  | { t: 'choices'; items: { id: string; glyph?: string; shape?: ShapeName }[] }
  /**
   * Desen döşemeleri — kare hücre matrisi olarak çizilen küçük şekiller.
   *
   * `on[r * n + c]` dolu hücreyi gösteriyor. Döndürme ve aynalama
   * ÜRETİMDE matris üzerinde yapılıyor, çizimde CSS dönüşümüyle değil.
   *
   * Gerekçe: `zihinde çevirme` egzersizi bir metin glifini (`◣`) döndürmek
   * üzerine kuruluydu ve açı çizim ağacına hiç ulaşmıyordu — `choices`
   * düğümünün dönme alanı yok. Doğru şık her tohumda temel şeklin birebir
   * aynısı çıkıyordu; yönerge "döndürülmüş hali hangisi" diyor, cevap ise
   * "aynısı" oluyordu. Dönüşümü glife uygulayarak düzeltmek, sorunu yazı
   * tipine taşımak olurdu: eğik çizilen bir glifin taban çizgisi ve sınır
   * kutusu fonta göre oynuyor, tablette ve yazıcıda farklı duruyor.
   * Matris hiçbirine bağlı değil.
   *
   * Yan kazanç: dönme yalnızca 90°'nin katları olabiliyor. Bu bir kısıt
   * değil, doğru olan: ara açı matris üzerinde tanımsız ve eski kodda
   * doğru şıkkı tek "eğik duran" şık yapıp görevi bakmakla çözülür hale
   * getiriyordu.
   */
  | { t: 'tiles'; n: number; items: { id: string; on: boolean[] }[]; pick?: boolean }

/* ------------------------------------------------------------------ */

export interface ExerciseParams {
  [key: string]: number | string | boolean
}

export interface ExerciseInstance {
  code: ExerciseCode
  version: string
  level: number
  seed: string
  params: ExerciseParams
  domains: Domain[]
  /** Çocuğa gösterilen yönerge */
  prompt: string
  /** İşitsel yönerge metni (varsa) — seslendirme için */
  spokenPrompt?: string
  render: RenderNode[]
  /** Doğru cevap. Kağıt kanalda uygulayıcı anahtarına basılır. */
  solution: unknown
  /** Dijital kanalda otomatik mi puanlanır */
  grading: 'auto' | 'manual'
  /** Tahmini süre — haftalık set dengelemek için */
  estimatedSeconds: number
  /** Kaç madde var: doğruluk oranının paydası */
  itemCount: number
}

export interface ExerciseTemplate {
  code: ExerciseCode
  title: string
  domains: Domain[]
  channels: Channel[]
  /** Zorluk 1–10 arası; her seviye için parametreler */
  params(level: number): ExerciseParams
  /** Aynı tohum + aynı seviye = aynı sayfa */
  generate(seed: number | bigint, level: number): ExerciseInstance
  /** Verilen cevabı puanlar (dijital kanal) */
  grade(instance: ExerciseInstance, answer: unknown): GradeResult
}

/**
 * Tek bir maddenin sonucu.
 *
 * `perItem` yalnızca "hangi maddede yanlış yaptı" diyor. Uygulayıcının
 * sorduğu soru ise şu: NE yazdı? Boş mu bıraktı, yanlış mı yazdı,
 * yoksa doğruyu bulup başka yere mi koydu? Üçü üç ayrı bulgu ve
 * doğru/yanlış ikilisi üçünü de aynı gösteriyor.
 */
export interface ItemResult {
  /** Madde kimliği: hücre ya da satır id'si; yoksa sıra numarası */
  id: string
  /** İnsan okunur beklenen cevap */
  beklenen: string
  /** Çocuğun verdiği cevap; boş bırakıldıysa null */
  verilen: string | null
  dogru: boolean
  /**
   * Beklenmeyen fazladan işaret.
   *
   * Küme görevlerinde (hücre işaretleme) çocuk doğruları bulmanın yanı
   * sıra yanlış hücreleri de işaretleyebilir. Bunlar "eksik madde"
   * değil, fazladan işarettir ve ayrı okunmalı: hepsini işaretleyip tam
   * puan almaya çalışmak da bir davranış örüntüsü.
   */
  fazladan?: boolean
}

export interface GradeResult {
  correct: number
  total: number
  accuracy: number
  /** Madde madde doğru/yanlış — geriye dönük uyumluluk için duruyor */
  perItem?: boolean[]
  /** Madde madde detay: ne beklendi, ne verildi */
  items?: ItemResult[]
}

/**
 * Üretim sözleşmesinin sürümü.
 *
 * Aynı tohumun aynı sayfayı vermesi şablonun KODUNA bağlı: şablon
 * değişince eski bir atama aynı kodla aynı tohumdan BAŞKA bir sayfa
 * üretir. O yüzden şablonların çıktısı her değiştiğinde burası artıyor
 * ve eski atamalar `surum_uyusmazligi` ile reddediliyor — sessizce
 * başka bir egzersize dönüşmeleri yerine.
 *
 * 1.1: Katman 0. Şekiller Unicode karakter olmaktan çıkıp çizilen
 * şekillere döndü, çözüm dizeleri şekil adlarına dönüştü
 * (`'●'` değil `'daire'`). Aynı sürümde `mental_rotation` ve
 * `matrix_pattern` yeniden yazılmıştı; o değişiklikte sürüm
 * artırılmamıştı, burada birlikte kapanıyor.
 */
export const EXERCISE_VERSION = '1.1'

/** Seviye 1–10 aralığına sıkıştırır */
export function clampLevel(level: number): number {
  return Math.min(10, Math.max(1, Math.round(level)))
}

/** Seviyeye göre iki uç arasında doğrusal değer */
export function scale(level: number, min: number, max: number): number {
  const l = clampLevel(level)
  return Math.round(min + ((max - min) * (l - 1)) / 9)
}

/**
 * Egzersizlerin Türkçe adları.
 *
 * MOTORDA DURUYOR, uygulamalarda değil. Panelde bir kopyası vardı ve
 * çocuk uygulamasına ikinci bir kopya yazıldığında yarısı eksik
 * kaldı: tablette ekranda `arith_fluency` yazıyordu. Kopya tutmanın
 * bedeli bu.
 *
 * Tipi `Record<ExerciseCode, string>`: yeni bir egzersiz kodu
 * eklendiğinde adı yazılmadıysa DERLEME HATASI veriyor. Eksik ad
 * artık çalışma zamanında ortaya çıkmıyor.
 */
export const EGZERSIZ_ADI: Record<ExerciseCode, string> = {
  scan_target: 'Hedef tarama',
  spot_difference: 'Farkı bul',
  odd_one_out: 'Farklı olanı bul',
  word_search: 'Kelime bulmaca',
  path_trace: 'Yol takibi',
  span_recall: 'Dizi hatırlama',
  memory_pairs: 'Eş bulma',
  symbol_code: 'Şekil–sembol eşleme',
  listening_steps: 'Yönerge takibi',
  reading_target: 'Okuyup sayma',
  matrix_pattern: 'Örüntü tamamlama',
  sequence_complete: 'Diziyi tamamla',
  logic_grid: 'Mantık ızgarası',
  sudoku_mini: 'Sayı bulmaca',
  category_sort: 'Gruplama',
  maze: 'Labirent',
  mental_rotation: 'Zihinde çevirme',
  mirror_image: 'Ayna görüntüsü',
  arith_fluency: 'Toplama çıkarma',
  opposite_rule: 'Tersini yap',
}

/** Kod tanınmıyorsa kodun kendisini döndürür — ekranda boşluk kalmasın */
export function egzersizAdi(kod: string): string {
  return (EGZERSIZ_ADI as Record<string, string>)[kod] ?? kod
}

/**
 * Egzersizin hangi alan ikonunu taşıdığı — çerçeve katmanı.
 *
 * Yedi `Domain`'den TÜRETİLMİYOR, elle yazılıyor. Türetmek denendi ve
 * yanlış çıktı: `maze`'in alanları `working_memory_visual` +
 * `selective_attention`, türetilse bellek ya da dikkat ikonu alırdı;
 * oysa labirent uzamsal bir iş. `symbol_code` aynı şekilde işlem
 * hızına düşüyor. Yedi alan ölçümün ekseni, bu dördü çocuğa ve
 * uygulayıcıya gösterilen gruplama; ikisi aynı şey değil.
 *
 * Tipi `Record<ExerciseCode, AlanKodu>`: yeni bir şablon alan
 * yazmazsa DERLEME HATASI veriyor — `EGZERSIZ_ADI` ile aynı gerekçe.
 */
export const EGZERSIZ_ALANI: Record<ExerciseCode, AlanKodu> = {
  scan_target: 'dikkat',
  spot_difference: 'dikkat',
  odd_one_out: 'dikkat',
  word_search: 'dikkat',
  path_trace: 'dikkat',
  span_recall: 'bellek',
  memory_pairs: 'bellek',
  symbol_code: 'bellek',
  listening_steps: 'bellek',
  reading_target: 'bellek',
  matrix_pattern: 'akil',
  sequence_complete: 'akil',
  logic_grid: 'akil',
  sudoku_mini: 'akil',
  category_sort: 'akil',
  maze: 'uzamsal',
  mental_rotation: 'uzamsal',
  mirror_image: 'uzamsal',
  /**
   * Bu ikisi `spatial.ts` dosyasında duruyor ama uzamsal DEĞİL.
   *
   * Dosya düzeni bir gruplama değil, yazılma sırası. Toplama çıkarma
   * işlem hızı ölçüyor, tersini yap ketleme; ikisinde de uzamda bir
   * şey yok. Küp ikonu "üç boyut" diyor ve bir toplama sayfasının
   * üstünde yalan söylüyordu. Dördün içinde en yakını akıl yürütme:
   * ikisi de bir kuralı uygulama işi.
   */
  arith_fluency: 'akil',
  opposite_rule: 'akil',
}

/** Kod tanınmıyorsa dikkat: ikon boş kalmasın, şerit kaymasın */
export function egzersizAlani(kod: string): AlanKodu {
  return (EGZERSIZ_ALANI as Record<string, AlanKodu>)[kod] ?? 'dikkat'
}
